import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  AttackResponse,
  BattleLogEntry,
  MarchEntry,
  OpponentEntry,
  ScoutReport,
} from "../types/marches";
import { castleKeys } from "./castle";

export const marchKeys = {
  opponents: ["opponents"] as const,
  active: ["marches"] as const,
  battles: ["battles"] as const,
};

export function useOpponents(enabled: boolean) {
  return useQuery({
    queryKey: marchKeys.opponents,
    queryFn: async () => {
      const res = await api.get<OpponentEntry[]>("/opponents");
      return res.data;
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useMarches() {
  return useQuery({
    queryKey: marchKeys.active,
    queryFn: async () => {
      const res = await api.get<MarchEntry[]>("/marches");
      return res.data;
    },
    refetchInterval: 5_000,
    staleTime: 2_000,
  });
}

export function useBattles(enabled: boolean) {
  return useQuery({
    queryKey: marchKeys.battles,
    queryFn: async () => {
      const res = await api.get<BattleLogEntry[]>("/battles");
      return res.data;
    },
    enabled,
    staleTime: 10_000,
  });
}

export function useScout() {
  return useMutation({
    mutationFn: async (targetId: string) => {
      const res = await api.post<ScoutReport>(`/scout/${targetId}`);
      return res.data;
    },
  });
}

// Battle stratagems — mirrors server-side Stratagem enum.
// Balanced applies no stat modifier and is the default.
export const Stratagem = {
  Balanced: 0,
  Aggressive: 1,
  Defensive: 2,
} as const;

export function useAttack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      defenderId,
      units,
      heroIds,
      stratagem,
    }: {
      defenderId: string;
      units: Record<string, number>;
      heroIds: string[];
      stratagem?: number;
    }) => {
      const res = await api.post<AttackResponse>(`/attack/${defenderId}`, {
        units,
        heroIds,
        stratagem: stratagem ?? Stratagem.Balanced,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: marchKeys.active });
      qc.invalidateQueries({ queryKey: castleKeys.state });
    },
  });
}

export function useRecallMarch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/marches/${id}/recall`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: marchKeys.active });
      qc.invalidateQueries({ queryKey: castleKeys.state });
    },
  });
}
